import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeMode = 'light' | 'dark' | 'system'

type ThemeStore = {
  mode: ThemeMode
  effectiveTheme: 'light' | 'dark'
  setMode: (mode: ThemeMode) => void
  initializeTheme: () => void
}

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const calculateEffectiveTheme = (mode: ThemeMode): 'light' | 'dark' => {
  if (mode === 'system') return getSystemTheme()
  return mode
}

const applyTheme = (theme: 'light' | 'dark') => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (theme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      mode: 'system',
      effectiveTheme: 'light',

      setMode: (mode: ThemeMode) => {
        const effectiveTheme = calculateEffectiveTheme(mode)
        applyTheme(effectiveTheme)
        set({ mode, effectiveTheme })
      },

      initializeTheme: () => {
        const { mode } = get()
        const effectiveTheme = calculateEffectiveTheme(mode)
        applyTheme(effectiveTheme)

        if (mode === 'system') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
          const handler = (e: MediaQueryListEvent) => {
            applyTheme(e.matches ? 'dark' : 'light')
            set({ effectiveTheme: e.matches ? 'dark' : 'light' })
          }
          mediaQuery.addEventListener('change', handler)
        }

        set({ effectiveTheme })
      },
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({ mode: state.mode }),
    }
  )
)
